/*
 * The homepage newsletter (session A5b): subscribe to the SAME Klaviyo list
 * the popup uses, so the offer the section states is the offer the shopper
 * gets. See sections/fz-home-newsletter.liquid for why.
 *
 * Klaviyo's client subscriptions endpoint is the one the popup itself calls:
 * public, keyed by the company id, CORS-enabled, and it answers 202 Accepted.
 * Only a 2xx shows the code. Anything else keeps the form, says so, and
 * leaves the Shopify form as the way through (a second submit posts it
 * natively), so a signup is never silently lost.
 *
 * Nothing here runs until the shopper submits; automated runs never submit.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

class FzNewsletter extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.input = this.querySelector('input[type="email"]');
    this.button = this.querySelector('button[type="submit"]');
    this.error = this.querySelector(".fz-home-newsletter__error");
    this.formBox = this.querySelector("[data-fz-newsletter-form]");
    this.success = this.querySelector("[data-fz-newsletter-success]");
    this.copy = this.querySelector("[data-fz-copy]");
    if (!this.form || !this.input) return;

    this.native = false;
    this.onSubmit = (event) => this.submit(event);
    this.form.addEventListener("submit", this.onSubmit);
    this.copy?.addEventListener("click", () => this.copyCode());
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.onSubmit);
  }

  say(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = !message;
    this.input.setAttribute("aria-invalid", message ? "true" : "false");
  }

  async submit(event) {
    if (this.native) return;
    event.preventDefault();

    const email = this.input.value.trim();
    if (!EMAIL.test(email)) {
      this.say(this.dataset.invalid || this.error?.dataset.invalid || "Please enter a valid email address.");
      this.input.focus();
      return;
    }
    this.say("");
    this.button?.setAttribute("aria-busy", "true");
    if (this.button) this.button.disabled = true;

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.dataset.company)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/vnd.api+json", revision: "2024-10-15" },
          body: JSON.stringify({
            data: {
              type: "subscription",
              attributes: {
                custom_source: this.dataset.source || "Homepage newsletter",
                profile: { data: { type: "profile", attributes: { email } } },
              },
              relationships: { list: { data: { type: "list", id: this.dataset.list } } },
            },
          }),
        },
      );
      if (!response.ok) throw new Error(String(response.status));
      this.formBox.hidden = true;
      this.success.hidden = false;
      this.success.focus({ preventScroll: true });
    } catch (e) {
      // Klaviyo could not be reached: keep the form, say so, and let a second
      // try go straight to Shopify's customer form.
      this.native = true;
      this.say(this.error?.dataset.failed || "That didn't go through. Please try again.");
    } finally {
      this.button?.removeAttribute("aria-busy");
      if (this.button) this.button.disabled = false;
    }
  }

  async copyCode() {
    const el = this.querySelector("[data-fz-code]");
    const code = el?.textContent?.trim();
    if (!code) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("no clipboard API");
      await navigator.clipboard.writeText(code);
      if (this.copy) this.copy.textContent = this.copy.dataset.copied || "Copied";
    } catch (e) {
      // No clipboard API, or the app refused it (some in-app browsers): the
      // button must not do nothing (A7 in-app risk IA-11). Select the code so
      // the phone's own copy menu is one press away, and say so.
      const range = document.createRange();
      range.selectNodeContents(el);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      if (this.copy) this.copy.textContent = this.copy.dataset.copyManual || "Selected: press and hold to copy";
    }
  }
}

if (!customElements.get("fz-newsletter")) customElements.define("fz-newsletter", FzNewsletter);
